import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { APP_NAME } from "@crewledger/shared/constants";
import {
  ENABLE_WORKER_DOCUMENTS_CONFIRMATION,
  type PlatformFeaturesResponse,
} from "@crewledger/shared/schemas";
import { useAuth } from "@/features/auth/auth-provider";
import { apiFetch } from "@/lib/api";
import {
  ConfirmationModal,
  InfoBanner,
  PageHeader,
  useToast,
} from "@/components/design";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function PlatformFeaturesPage() {
  const { accessGate, email, refreshSession } = useAuth();
  const { toast } = useToast();
  const [features, setFeatures] = useState<PlatformFeaturesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    document.title = `Platform features — ${APP_NAME}`;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: PlatformFeaturesResponse }>(
        "/api/admin/platform/features",
      );
      setFeatures(res.data);
      setForbidden(false);
    } catch {
      setForbidden(true);
      setFeatures(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function applyToggle(enabled: boolean, confirmationPhrase?: string) {
    setSaving(true);
    try {
      const res = await apiFetch<{ data: PlatformFeaturesResponse }>(
        "/api/admin/platform/features/worker-documents",
        {
          method: "PATCH",
          body: JSON.stringify({
            enabled,
            reason: reason.trim() || null,
            confirmationPhrase,
          }),
        },
      );
      setFeatures(res.data);
      setConfirmOpen(false);
      setReason("");
      await refreshSession();
      toast({
        title: enabled ? "Worker Documents enabled" : "Worker Documents disabled",
        description: enabled
          ? "Visible to platform admins only."
          : "Hidden from all users and routes blocked.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Could not update feature",
        description: error instanceof Error ? error.message : "Request failed",
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  if (!accessGate?.isPlatformAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Access denied</CardTitle>
            <CardDescription>Platform admin access is required.</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/admin" className="text-sm font-semibold hover:underline">
              Back to admin
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <PageHeader
        title="Platform features"
        description={`Internal Werkio controls for ${email ?? "platform admin"}.`}
      />
      <Link to="/admin" className="text-sm font-semibold text-blue-700 hover:underline">
        ← Back to admin
      </Link>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : forbidden || !features ? (
        <InfoBanner variant="warning">Could not load platform features.</InfoBanner>
      ) : (
        <Card>
          <CardHeader>
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-800">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <CardTitle>Worker Documents</CardTitle>
            <CardDescription>
              Worker Documents is implemented but hidden while legal/privacy review is ongoing.
              When enabled, access remains limited to Werkio platform admins only. Customers and
              company admins cannot access this module.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoBanner variant="warning">
              This module may contain sensitive HR, tax, and identity document workflows. Enable
              only for internal validation. Do not expose to customers until legal review is
              complete.
            </InfoBanner>
            <div className="space-y-2">
              <Label htmlFor="feature-reason">Reason (optional)</Label>
              <Input
                id="feature-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you changing this flag?"
              />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
              <div>
                <p className="text-sm font-semibold text-navy-deep">Enable Worker Documents</p>
                <p className="text-xs text-muted-foreground">
                  Current status: {features.workerDocumentsEnabled ? "Enabled" : "Disabled"}
                </p>
              </div>
              {features.workerDocumentsEnabled ? (
                <Button
                  variant="outline"
                  disabled={saving}
                  onClick={() => void applyToggle(false)}
                >
                  {saving ? "Working…" : "Disable"}
                </Button>
              ) : (
                <Button disabled={saving} onClick={() => setConfirmOpen(true)}>
                  Enable
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() =>
          void applyToggle(true, ENABLE_WORKER_DOCUMENTS_CONFIRMATION)
        }
        title="Enable Worker Documents?"
        description="This will make the Worker Documents module visible to platform admins only. It will not be visible to customers or company admins."
        confirmLabel="Enable"
        requirePhrase={ENABLE_WORKER_DOCUMENTS_CONFIRMATION}
        loading={saving}
      />
    </div>
  );
}
